import { backgroundLayers, overlayers } from "config/baseLayers";
import { LatLngLiteral, BoundsLiteral } from "leaflet";
import { useSearchParams } from "react-router-dom";
import { MapPosition } from "types/map";
import { TileLayer, WMSLayer } from "types/types";
import { convertStringArrToNumberArr } from "utils/ArrayUtils";
import { mapURLSearchParams } from "utils/SearchParamUtils";

const initialPosition: MapPosition = {
  lat: 50.9443545965443,
  lng: 4.0691421853899,
  zoom: 17,
};

export const initialBounds: BoundsLiteral = [
  [50.658897, 2.489921],
  [51.55559, 5.923148],
];

export function useMapPosition(initial = initialPosition) {
  const [params] = useSearchParams();

  return {
    lat: parseFloat(params.get("lat") || initial.lat.toString()),
    lng: parseFloat(params.get("lon") || initial.lng.toString()),
    zoom: parseFloat(params.get("zoom") || initial.zoom.toString()),
  };
}

export function useMapPoint(): [
  LatLngLiteral | null,
  (point: LatLngLiteral | null) => void
] {
  const [params, setParams] = useSearchParams();
  const rawPoint = params.get("point");
  const point = rawPoint
    ? arrayToLatLng(rawPoint.split(",").map(parseFloat))
    : null;

  function arrayToLatLng(pointArray: number[]): LatLngLiteral {
    const [lat, lng] = pointArray;
    return { lat, lng };
  }

  function updateParams(point: LatLngLiteral | null) {
    const newParams = mapURLSearchParams(params, ["point"]);
    if (point) {
      newParams.set("point", [point.lat, point.lng].join(","));
    }

    setParams(newParams);
  }

  return [point, updateParams];
}

export function useSelectedBackgroundLayer() {
  const { selectedBackgroundLayerId } = useBackgroundLayerParams();
  const availableBackgroundLayers = backgroundLayers;
  const backgroundLayerUrl = availableBackgroundLayers.find(
    (backgroundLayer) => backgroundLayer.id === selectedBackgroundLayerId
  );
  const defaultbackgroundLayer = availableBackgroundLayers.find(
    (backgroundLayer) => backgroundLayer.default
  );

  if (backgroundLayerUrl) return backgroundLayerUrl;
  if (defaultbackgroundLayer) return defaultbackgroundLayer;

  const [firstBackgroundLayer] = availableBackgroundLayers || [];
  return firstBackgroundLayer;
}

export function useBackgroundLayerParams() {
  const [params, setParams] = useSearchParams();
  const selectedBackgroundLayerId = +(params.get("bgl") || "");

  function updateBackgroundLayerParam(backgroundLayerId: number) {
    const newParams = new URLSearchParams(params);
    newParams.set("bgl", backgroundLayerId.toString());

    setParams(newParams);
  }

  return { selectedBackgroundLayerId, updateBackgroundLayerParam };
}

export function useOverlayerParams() {
  const [params, setParams] = useSearchParams();
  const selectedOverlayerIds = convertStringArrToNumberArr(params.getAll("ol"));

  function updateOverlayerParams(overlayerId: number, checked: boolean) {
    const newParams = checked
      ? new URLSearchParams(params)
      : mapURLSearchParams(params, ["ol"]);

    if (checked) {
      newParams.append("ol", overlayerId.toString());
    }

    if (!checked) {
      const filteredOverlayerIds = selectedOverlayerIds.filter(
        (value) => value !== overlayerId
      );
      filteredOverlayerIds.forEach((id) =>
        newParams.append("ol", id.toString())
      );
    }

    setParams(newParams);
  }

  return {
    selectedOverlayerIds,
    updateOverlayerParams,
  };
}

export function useSelectedOverlayers() {
  const availableOverlayers = overlayers;
  const { selectedOverlayerIds } = useOverlayerParams();
  const selectedOverlayers = availableOverlayers.filter((overlayer) =>
    selectedOverlayerIds.includes(overlayer.id)
  );

  return selectedOverlayers;
}

export function isWMSLayer(layer: WMSLayer | TileLayer): layer is WMSLayer {
  return layer.hasOwnProperty("wms");
}

export function useLanduseLayer() {
  const landuseLayer = backgroundLayers.find((backgroundlayer) =>
    backgroundlayer.name.toLowerCase().includes("bodembedekking")
  );
  if (landuseLayer && isWMSLayer(landuseLayer)) return landuseLayer;
}
